import { BLOCKS } from '@contentful/rich-text-types';
import React from 'react';

import Loader from '../UI/Loader/Loader';
import RichText from '../UI/RichText';
import Checkmark from '../../images/Checkmark.svg';
import { RecaptchaSize } from '../../types';

import FormField from './FormField/FormField';
import FormSectionHeading from './FormSectionHeading/FormSectionHeading';
import {
  CheckMarkImg,
  ErrorSection,
  FormWrapper,
  Headline,
  LoadingWrapper,
  StyledForm,
  SubHeadline,
  Submitted,
} from './styles';
import {
  FormFieldI,
  FormFieldSectionHeadingI,
  FormFieldType,
  FormLayoutProps,
} from './types';
import { getPreselectedDropdownValue } from './utils';

const successTitleOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Headline>{children}</Headline>,
  },
};
const successSubtitleOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <SubHeadline>{children}</SubHeadline>
    ),
  },
};

const FormLayout = ({
  formFields,
  stage,
  clearErrors,
  resubmitForm,
  submitForm,
  register,
  control,
  recaptchaRef,
  recaptchaSize = RecaptchaSize.NORMAL,
  isValid,
  loading,
  generalError,
  errors,
  error,
  touchedFields,
  successSection,
  testId,
  renderSuccessCta,
  onDropdownChange,
  dropdownData,
  activeDropdown,
  setActiveDropdown,
  preselectedValues,
  conflictError,
}: FormLayoutProps) => {
  const isConflictError = error && error?.response?.status === 409;
  return (
    <FormWrapper>
      <div className="wrapper">
        {stage === 'form' && (
          <StyledForm
            id={testId}
            onSubmit={(e) => {
              clearErrors();
              resubmitForm();
              submitForm(e);
            }}
          >
            {formFields.map((formField) => {
              if (formField.type === FormFieldType.SECTION_HEADING) {
                const field = formField as FormFieldSectionHeadingI;
                return (
                  <FormSectionHeading
                    formField={field}
                    key={field.heading.id}
                  />
                );
              }
              const field = formField as FormFieldI;
              const preselectedValue = getPreselectedDropdownValue(
                field?.fieldName,
                field?.fieldValues,
                preselectedValues,
              );
              let selectedValue: string | undefined;
              if (
                dropdownData
                && field.fieldName
                && dropdownData[field.fieldName]
              ) {
                selectedValue = dropdownData[field.fieldName];
              } else if (preselectedValue) {
                selectedValue = preselectedValue;
                if (onDropdownChange) {
                  onDropdownChange(field?.fieldName, preselectedValue);
                }
              }
              return (
                <FormField
                  key={field.fieldName ?? field.schemaName}
                  formField={field}
                  register={register}
                  control={control}
                  recaptchaRef={recaptchaRef}
                  recaptchaSize={recaptchaSize}
                  isFormValid={isValid}
                  loading={loading}
                  errorSection={generalError}
                  error={
                    field.schemaName ? errors[field.schemaName] : undefined
                  }
                  valid={
                    field.schemaName
                      ? touchedFields[field.schemaName]
                        && !errors?.[field.schemaName]
                      : undefined
                  }
                  activeDropdown={activeDropdown}
                  setActiveDropdown={setActiveDropdown}
                  onDropdownChange={onDropdownChange}
                  selectedValue={selectedValue}
                />
              );
            })}
            {error && generalError && !isConflictError && (
              <ErrorSection>{generalError?.errorMessage}</ErrorSection>
            )}
            {error && isConflictError && (
              <ErrorSection>{conflictError?.errorMessage}</ErrorSection>
            )}
          </StyledForm>
        )}
        {stage === 'loading' && (
          <LoadingWrapper>
            <Loader className="small m-auto" />
          </LoadingWrapper>
        )}
        {stage === 'submitted' && successSection && (
          <Submitted>
            <RichText
              document={successSection.heading}
              options={successTitleOptions}
            />
            <RichText
              document={successSection.subHeading}
              options={successSubtitleOptions}
            />
            {renderSuccessCta ? (
              renderSuccessCta()
            ) : (
              <CheckMarkImg
                src={Checkmark}
                alt=""
                data-testid={
                  testId ? `${testId}-form-checkmark` : 'form-checkmark'
                }
              />
            )}
          </Submitted>
        )}
      </div>
    </FormWrapper>
  );
};

export default FormLayout;
